import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api.config';
import SearchableDropdown from './SearchableDropdown';
import { nationalities } from '../constants/nationalities';

const CreateNetworkUserModal = ({ isOpen, onClose, onSuccess, networkId, isEdit = false, initialData = null }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        additionalPhoneNumber: '',
        physicalAddress: '',
        nationality: '',
        aadharNumber: '',
        panNumber: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (isEdit && initialData) {
                setFormData({
                    name: initialData.name || '',
                    email: initialData.email || '',
                    password: '',
                    phoneNumber: initialData.phoneNumber || '',
                    additionalPhoneNumber: initialData.additionalPhoneNumber || '',
                    physicalAddress: initialData.investorDetails?.physicalAddress || '',
                    nationality: initialData.investorDetails?.nationality || '',
                    aadharNumber: initialData.investorDetails?.aadharNumber || '',
                    panNumber: initialData.investorDetails?.panNumber || ''
                });
            } else {
                setFormData({
                    name: '',
                    email: '',
                    password: '',
                    phoneNumber: '',
                    additionalPhoneNumber: '',
                    physicalAddress: '',
                    nationality: '',
                    aadharNumber: '',
                    panNumber: ''
                });
            }
            setErrors({});
        }
    }, [isOpen, isEdit, initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                additionalPhoneNumber: formData.additionalPhoneNumber,
                role: 'MANAGEMENT', // Set role to MANAGEMENT for read-only network users
                physicalAddress: formData.physicalAddress,
                nationality: formData.nationality,
                aadharNumber: formData.aadharNumber,
                panNumber: formData.panNumber,
                investorNetworkId: networkId // Link to current network
            };

            if (!isEdit && formData.password) {
                payload.password = formData.password;
            }

            let response;
            if (isEdit) {
                response = await fetch(getApiUrl(`admin/users/${initialData.id}`), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await fetch(getApiUrl('admin/users/create'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (response.ok) {
                onSuccess();
                handleClose();
            } else {
                const errorText = await response.text();
                let errorMessage = errorText;

                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorText;

                    if (errorJson.validationErrors) {
                        setErrors(errorJson.validationErrors);
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    // Use raw text if not JSON
                }

                const newErrors = {};
                if (errorMessage.toLowerCase().includes('aadhaar') || errorMessage.toLowerCase().includes('aadhar')) {
                    newErrors.aadharNumber = 'This Aadhaar number is already registered.';
                } else if (errorMessage.toLowerCase().includes('pan')) {
                    newErrors.panNumber = 'This PAN number is already linked to an account.';
                } else {
                    newErrors.general = errorMessage;
                }

                setErrors(newErrors);
                console.error(`Failed to ${isEdit ? 'update' : 'create'} user:`, errorMessage);
            }
        } catch (error) {
            console.error('Error creating user:', error);
            setErrors({ general: 'Network error occurred.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phoneNumber: '',
            additionalPhoneNumber: '',
            physicalAddress: '',
            nationality: '',
            aadharNumber: '',
            panNumber: ''
        });
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-lg">
                    <h2 className="text-xl font-bold">
                        {isEdit ? 'Edit User' : 'Add User'}
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                        {isEdit ? 'Update user details' : 'Add a new user to this network (Read-only access)'}
                    </p>
                </div>

                {errors.general && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{errors.general}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Common Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={isEdit}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                placeholder="User name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                disabled={isEdit}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                placeholder="email@example.com"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {!isEdit && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="••••••••"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Phone</label>
                        <input
                            type="tel"
                            name="additionalPhoneNumber"
                            value={formData.additionalPhoneNumber}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 234 567 8900"
                        />
                    </div>

                    <div className="border-t pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">User Details</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                            <SearchableDropdown
                                name="nationality"
                                value={formData.nationality}
                                onChange={handleChange}
                                options={nationalities}
                                placeholder="Select Nationality"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                            <textarea
                                name="physicalAddress"
                                value={formData.physicalAddress}
                                onChange={handleChange}
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter physical/residential address"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Aadhar Number *
                                    <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                </label>
                                <input
                                    type="text"
                                    name="aadharNumber"
                                    value={formData.aadharNumber}
                                    onChange={handleChange}
                                    required
                                    maxLength="12"
                                    pattern="[0-9]{12}"
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.aadharNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                        }`}
                                    placeholder="XXXX XXXX XXXX"
                                />
                                {errors.aadharNumber && (
                                    <p className="text-red-500 text-xs mt-1">{errors.aadharNumber}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number *
                                    <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                </label>
                                <input
                                    type="text"
                                    name="panNumber"
                                    value={formData.panNumber}
                                    onChange={handleChange}
                                    required
                                    maxLength="10"
                                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase ${errors.panNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                        }`}
                                    placeholder="ABCDE1234F"
                                    style={{ textTransform: 'uppercase' }}
                                />
                                {errors.panNumber && (
                                    <p className="text-red-500 text-xs mt-1">{errors.panNumber}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50"
                        >
                            {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateNetworkUserModal;
